import { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";

import { AppRoutes } from "@/routing/Routes";
import { Toast } from "@/components/shared/Toast";
import { FlowProvider } from "@/contexts/FlowProvider";
import { AuthProvider } from "@/contexts/AuthProvider";
import { preloadFFmpeg } from "./utils/videoEditing";
import { DeployContextProvider } from "./contexts/DeployProvider";
import { TutorialProvider } from "@/contexts/TutorialProvider";
import { AnalyticsProvider } from "@/contexts/AnalyticsProvider";
import { FpsProvider } from "@/contexts/FpsProvider";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalProvider";
import { ProjectSelectorProvider } from "@/contexts/ProjectSelectorProvider";
import { UrlModalProvider } from "@/contexts/UrlModalProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useCheckoutCompletion } from "@/hooks/useCheckoutCompletion";
import LoginModal from "./components/auth/LoginModal";

export function App() {
    // Pre-load FFmpeg as soon as the app starts
    useEffect(() => {
        preloadFFmpeg() // FFmpeg preload completed - trimming will be instant
            .catch((error) => console.warn("🎬 [FFmpeg] Preload failed:", error));
    }, []);

    return (
        <BrowserRouter>
            <ProjectSelectorProvider>
                <AuthProvider>
                    <AnalyticsProvider>
                        <UpgradeModalProvider>
                            <FpsProvider>
                                <FlowProvider>
                                    <DeployContextProvider>
                                        <TutorialProvider>
                                            <UrlModalProvider>
                                                <AppContent />
                                            </UrlModalProvider>
                                        </TutorialProvider>
                                    </DeployContextProvider>
                                </FlowProvider>
                            </FpsProvider>
                        </UpgradeModalProvider>
                    </AnalyticsProvider>
                </AuthProvider>
            </ProjectSelectorProvider>
        </BrowserRouter>
    );
}

const AppContent = () => {
    const { isLoginModalOpen, closeLoginModal, guestAuthInitialized, initializeGuestAuth } =
        useAuth();

    // Initialize guest auth for all unauthenticated users
    // This enables API calls (like inference) to work without login
    useEffect(() => {
        if (!guestAuthInitialized) {
            initializeGuestAuth();
        }
    }, [guestAuthInitialized, initializeGuestAuth]);

    // Handle Stripe checkout completion redirect
    useCheckoutCompletion();

    return (
        <>
            <AppRoutes />
            <Toast />
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
        </>
    );
};
